import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import type { StorefrontSearchReport } from '../../app/models/Reports';
import { getVisibleFitmentFields, normalizeFitmentKey } from '../../app/utils/fitmentFields';
import { FITMENT_CATALOG_LABEL_CHANGED, getCachedFitmentCatalogLabel, getStoredFitmentCatalogLabel, setCachedFitmentCatalogLabel } from '../../app/utils/fitmentCatalogLabel';
import { getSearchLabel } from '../reports/VehicleReportDetails';
import { SearchActivityChart } from '../reports/SearchActivitySummary';

// These are distinct catalog year values, not vehicle counts or a validation verdict.
export const getUnusualVehicleYears = (years: string[], currentYear: number) =>
    Array.from(new Set(years.map(year => year.trim())))
        .filter(year => /^\d+$/.test(year) && Number(year) > currentYear + 2)
        .sort((a, b) => Number(a) - Number(b));

const count = (value: number) => value.toLocaleString();
const reportDate = (value: string) => new Date(`${value.slice(0, 10)}T00:00:00`)
    .toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
const reportDays = (report?: StorefrontSearchReport) => {
    if (!report) return 30;
    // UTC dates avoid daylight-saving offsets changing the length of a period.
    const days = (Date.parse(report.to.slice(0, 10)) - Date.parse(report.from.slice(0, 10))) / 86400000 + 1;
    return Number.isFinite(days) ? Math.max(1, Math.round(days)) : 30;
};

const SearchMetric = ({ label, value, previous, compare, decimal = false }: {
    label: string; value: number; previous: number; compare: boolean; decimal?: boolean;
}) => {
    const change = previous === 0 ? (value === 0 ? 0 : null) : Math.round((value - previous) / previous * 100);
    return (
        <s-stack gap="small-300">
            <s-text color="subdued">{label}</s-text>
            <s-stack direction="inline" gap="small-200" alignItems="baseline">
                <s-text fontSize="large-100" fontWeight="bold">{value.toLocaleString(undefined, { maximumFractionDigits: decimal ? 1 : 0 })}</s-text>
                {compare && <s-text tone={change === null || change === 0 ? 'auto' : change < 0 ? 'critical' : 'success'}
                    accessibilityVisibility="hidden">
                    {change === null ? 'New' : `${change > 0 ? '+' : ''}${change}%`}
                </s-text>}
                {compare && <s-text accessibilityVisibility="exclusive">{change === null
                    ? 'New compared with the previous period'
                    : `${Math.abs(change)} percent ${change < 0 ? 'decrease' : 'increase'} from the previous period`}</s-text>}
            </s-stack>
        </s-stack>
    );
};

const yearCheckModalId = 'pf-vehicle-year-check';

// One row of a dashboard shortcut card.
const ShortcutRow = ({ icon, label, detail, onClick }: { icon: string; label: string; detail: string; onClick: () => void }) => (
    <s-clickable onClick={onClick} padding="base">
        <s-grid gridTemplateColumns="auto 1fr auto" gap="small-200" alignItems="center">
            <s-icon type={icon as any} />
            <s-stack gap="small-500">
                <s-text fontWeight="medium">{label}</s-text>
                <s-text color="subdued">{detail}</s-text>
            </s-stack>
            <s-icon type="chevron-right" color="subdued" />
        </s-grid>
    </s-clickable>
);

const CardHeading = ({ title, detail }: { title: string; detail: string }) => (
    <s-box padding="base" background="subdued">
        <s-stack gap="small-500">
            <s-heading>{title}</s-heading>
            <s-text color="subdued">{detail}</s-text>
        </s-stack>
    </s-box>
);

export default function DashboardOverview() {
    const navigate = useNavigate();
    const [report, setReport] = useState<StorefrontSearchReport>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [reload, setReload] = useState(0);
    const [unusualYears, setUnusualYears] = useState<string[]>([]);
    const [checkLoading, setCheckLoading] = useState(true);
    const [checkError, setCheckError] = useState(false);
    const [checkReload, setCheckReload] = useState(0);
    const [yearFieldAvailable, setYearFieldAvailable] = useState(false);
    const [checkedYearCount, setCheckedYearCount] = useState(0);
    const [vehicleCatalogLabel, setVehicleCatalogLabel] = useState(() => getStoredFitmentCatalogLabel() ?? '');
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const handleLabelChange = (event: Event) => {
            const label = (event as CustomEvent<string>).detail;
            if (label) setVehicleCatalogLabel(label);
        };
        window.addEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
        return () => window.removeEventListener(FITMENT_CATALOG_LABEL_CHANGED, handleLabelChange);
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(undefined);
        const load = async () => {
            try {
                const result = await agent.Reports.searchActivity({ days: 30, completedDaysOnly: true, limit: 5 });
                if (!cancelled) setReport(result);
            } catch (failure) {
                const message = await getApiErrorMessage(failure, 'Unable to load search activity.');
                if (!cancelled) setError(message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [reload]);

    useEffect(() => {
        const controller = new AbortController();
        setCheckLoading(true);
        setCheckError(false);
        const load = async () => {
            try {
                const catalog = await agent.Settings.fitmentCatalog();
                if (controller.signal.aborted) return;
                setVehicleCatalogLabel(setCachedFitmentCatalogLabel(catalog.name));
                const keys = getVisibleFitmentFields(catalog).map(normalizeFitmentKey);
                setYearFieldAvailable(keys.includes('year'));
                if (!keys.includes('year')) {
                    setUnusualYears([]);
                    setCheckedYearCount(0);
                    return;
                }
                // Request just the year facet, first in cascade order, across the full catalog.
                const result = await agent.Vehicles.filterOptions('', '', '', {},
                    ['year', ...keys.filter(key => key !== 'year')], ['year'], controller.signal);
                if (!controller.signal.aborted) {
                    const years = result.optionsByField?.year ?? result.years ?? [];
                    setCheckedYearCount(new Set(years.map(year => year.trim()).filter(year => /^\d+$/.test(year))).size);
                    setUnusualYears(getUnusualVehicleYears(years, currentYear));
                }
            } catch {
                if (!controller.signal.aborted) {
                    setVehicleCatalogLabel(getCachedFitmentCatalogLabel());
                    setCheckError(true);
                }
            } finally {
                if (!controller.signal.aborted) setCheckLoading(false);
            }
        };
        void load();
        return () => controller.abort();
    }, [currentYear, checkReload]);

    const days = reportDays(report);
    const total = report?.totalSearches ?? 0;
    const top = (Array.isArray(report?.topSearches) ? report?.topSearches ?? [] : []).filter(Boolean).slice(0, 5);
    const otherSearches = Math.max(0, total - top.reduce((sum, row) => sum + row.searchCount, 0));
    const otherVehicles = Math.max(0, (report?.uniqueSearchCount ?? 0) - top.length);
    const compare = Boolean(report?.comparisonEnabled && report.dataAvailableFrom && report.previousFrom
        && report.previousFrom.slice(0, 10) >= report.dataAvailableFrom.slice(0, 10));
    const checkStatus = checkLoading ? 'Checking years…' : checkError ? 'Check unavailable'
        : !yearFieldAvailable ? 'No visible year field in this catalog'
        : checkedYearCount === 0 ? 'No numeric vehicle years to check'
        : unusualYears.length > 0 ? `${count(unusualYears.length)} unusual vehicle ${unusualYears.length === 1 ? 'year' : 'years'}`
        : 'No unusual future years found';
    const reportUrl = (kind?: string) => {
        const params = new URLSearchParams();
        if (kind) params.set('report', kind);
        if (report) {
            params.set('from', report.from.slice(0, 10));
            params.set('to', report.to.slice(0, 10));
        }
        const query = params.toString();
        return `/reports${query ? `?${query}` : ''}`;
    };

    return (
        <s-stack gap="base">
            <s-query-container>
                <s-grid gridTemplateColumns="@container (inline-size > 640px) 1fr 1fr, 1fr" gap="base" alignItems="stretch">
                    <s-section accessibilityLabel="Fitment catalog" padding="none">
                        <CardHeading title="Fitment catalog" detail="Keep product compatibility and vehicle data up to date." />
                        <s-divider />
                        <ShortcutRow icon="product" label="Product fitment" detail="Review coverage and compatibility" onClick={() => navigate('/products')} />
                        <s-divider />
                        <ShortcutRow icon="data-table" label={vehicleCatalogLabel || '\u00a0'} detail="Add, edit, and organize vehicles" onClick={() => navigate('/vehicles')} />
                    </s-section>
                    <s-section accessibilityLabel="Catalog insights" padding="none">
                        <CardHeading title="Catalog insights" detail="Find coverage gaps and review your catalog’s next steps." />
                        <s-divider />
                        <ShortcutRow icon="chart-vertical" label="Coverage opportunities" detail="Find searches with few matching products" onClick={() => navigate(reportUrl('coverage_opportunities'))} />
                        <s-divider />
                        <s-box padding="base">
                            <s-grid gridTemplateColumns="auto 1fr auto" gap="small-200" alignItems="center">
                                <s-icon type={!checkLoading && !checkError && checkedYearCount > 0 && unusualYears.length === 0 ? 'check-circle' : 'calendar'} />
                                <s-stack gap="small-500">
                                    <s-text fontWeight="medium">Vehicle year check</s-text>
                                    {unusualYears.length > 0 && !checkLoading && !checkError
                                        ? <s-stack direction="inline"><s-badge tone="warning">{checkStatus}</s-badge></s-stack>
                                        : <s-text color="subdued">{checkStatus}</s-text>}
                                </s-stack>
                                {checkLoading ? null : checkError
                                    ? <s-button variant="tertiary" onClick={() => setCheckReload(value => value + 1)}>Retry</s-button>
                                    : unusualYears.length > 0
                                        ? <s-button variant="tertiary" accessibilityLabel={`Review ${checkStatus}`} commandFor={yearCheckModalId} command="--show">Review</s-button>
                                        : <s-button variant="tertiary" commandFor={yearCheckModalId} command="--show">View check</s-button>}
                            </s-grid>
                        </s-box>
                    </s-section>
                </s-grid>
            </s-query-container>

            <s-section accessibilityLabel="Storefront search activity" padding="none">
                <s-box padding="base">
                    <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                        <s-stack gap="small-500">
                            <s-heading>Storefront search activity</s-heading>
                            <s-text color="subdued">Last {days} complete days{report ? ` · ${reportDate(report.from)} – ${reportDate(report.to)}` : ''}</s-text>
                        </s-stack>
                        <s-button onClick={() => navigate(reportUrl())}>View reports</s-button>
                    </s-grid>
                </s-box>
                {loading ? (
                    <s-box padding="base" minBlockSize="160px">
                        <s-stack direction="inline" gap="small-200" alignItems="center" accessibilityRole="status">
                            <s-spinner size="base" accessibilityLabel="Loading search activity" />
                            <s-text color="subdued">Loading search activity…</s-text>
                        </s-stack>
                    </s-box>
                ) : error ? (
                    <s-box padding="base">
                        <s-banner tone="critical" heading="Search activity is temporarily unavailable">
                            {error}
                            <s-button slot="secondary-actions" onClick={() => setReload(value => value + 1)}>Try again</s-button>
                        </s-banner>
                    </s-box>
                ) : (
                    <>
                        <s-box paddingInline="base" paddingBlockEnd="base">
                            <s-query-container>
                                <s-grid gridTemplateColumns="@container (inline-size > 520px) 1fr 1fr 1fr, 1fr" gap="base">
                                    <SearchMetric label="Total searches" value={total}
                                        previous={report?.previousTotalSearches ?? 0} compare={compare} />
                                    <SearchMetric label="Unique vehicles searched" value={report?.uniqueSearchCount ?? 0}
                                        previous={report?.previousUniqueSearchCount ?? 0} compare={compare} />
                                    <SearchMetric label="Average searches / day" value={total / days} decimal
                                        previous={(report?.previousTotalSearches ?? 0) / days} compare={compare} />
                                </s-grid>
                            </s-query-container>
                        </s-box>
                        <s-divider />
                        <s-box padding="base">
                            <s-query-container>
                                <s-grid gridTemplateColumns="@container (inline-size > 760px) 1fr 1fr, 1fr" gap="large">
                                    <s-stack gap="small-200">
                                        <s-stack gap="small-500">
                                            <s-heading>Daily search volume</s-heading>
                                            <s-text color="subdued">Most recent 7 complete days</s-text>
                                        </s-stack>
                                        <SearchActivityChart report={report} days={7} compact embedded variant="line" />
                                    </s-stack>
                                    <s-stack gap="small-200">
                                        <s-stack gap="small-500">
                                            <s-heading>Top vehicle searches</s-heading>
                                            <s-text color="subdued">Ranked over the last {days} complete days</s-text>
                                        </s-stack>
                                        {top.length === 0 ? (
                                            <s-text color="subdued">No vehicle searches were recorded in this range.</s-text>
                                        ) : (
                                            <s-table variant="list">
                                                <s-table-header-row>
                                                    <s-table-header listSlot="primary">Vehicle</s-table-header>
                                                    <s-table-header listSlot="secondary" format="numeric">Searches</s-table-header>
                                                    <s-table-header listSlot="labeled" format="numeric">Products</s-table-header>
                                                    <s-table-header listSlot="labeled">Share</s-table-header>
                                                </s-table-header-row>
                                                <s-table-body>
                                                    {top.map((search, index) => {
                                                        const label = getSearchLabel(search);
                                                        const share = total > 0 ? Math.min(100, search.searchCount / total * 100) : 0;
                                                        const fields = Object.fromEntries(Object.entries(search.fieldValues ?? {})
                                                            .filter(([key, value]) => key.trim() && value?.trim()));
                                                        const productPath = `/products?${new URLSearchParams({ fieldValues: JSON.stringify(fields) })}`;
                                                        return (
                                                            <s-table-row key={`${label}-${index}`}>
                                                                <s-table-cell>
                                                                    {Object.keys(fields).length > 0
                                                                        ? <s-link accessibilityLabel={`View products matching ${label}`} onClick={() => navigate(productPath)}>{label}</s-link>
                                                                        : label}
                                                                </s-table-cell>
                                                                <s-table-cell>{count(search.searchCount)}</s-table-cell>
                                                                <s-table-cell>
                                                                    {/* Searched vehicles with no products are coverage gaps. */}
                                                                    <s-text tone={search.currentMappedProductCount === 0 ? 'caution' : 'auto'}>
                                                                        {count(search.currentMappedProductCount ?? 0)}
                                                                    </s-text>
                                                                </s-table-cell>
                                                                <s-table-cell>
                                                                    <s-grid gridTemplateColumns="1fr auto" gap="small-200" alignItems="center">
                                                                        <s-progress accessibilityLabel={`${Math.round(share)} percent of searches`} value={share} max={100} />
                                                                        <s-text color="subdued">{Math.round(share)}%</s-text>
                                                                    </s-grid>
                                                                </s-table-cell>
                                                            </s-table-row>
                                                        );
                                                    })}
                                                </s-table-body>
                                            </s-table>
                                        )}
                                        {otherVehicles > 0 && (
                                            <s-text color="subdued">
                                                {count(otherVehicles)} other {otherVehicles === 1 ? 'vehicle' : 'vehicles'} · {count(otherSearches)} searches
                                            </s-text>
                                        )}
                                    </s-stack>
                                </s-grid>
                            </s-query-container>
                        </s-box>
                        <s-divider />
                        <s-box padding="base" background="subdued">
                            <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
                                <s-text color="subdued">{top.length > 0 && total > 0
                                    ? `${getSearchLabel(top[0])} accounts for ${Math.round(top[0].searchCount / total * 100)}% of searches.`
                                    : 'Review demand and fitment coverage in Reports.'}</s-text>
                                <s-link onClick={() => navigate(reportUrl('coverage_opportunities'))}>Review coverage</s-link>
                            </s-grid>
                        </s-box>
                    </>
                )}
            </s-section>

            <s-modal id={yearCheckModalId} heading="Vehicle year check">
                <s-stack gap="small-200" accessibilityLabel="Unusual vehicle years">
                    <s-text color="subdued">{checkStatus}.</s-text>
                    <s-text color="subdued">{!yearFieldAvailable
                        ? 'This catalog has no enabled, visible year field, so the year check does not apply.'
                        : `${count(checkedYearCount)} distinct numeric year values checked. Years up to ${currentYear + 2} pass this check.`}</s-text>
                    {unusualYears.map(year => (
                        <s-stack key={year} direction="inline" justifyContent="space-between" alignItems="center">
                            <s-text fontWeight="medium">{year}</s-text>
                            <s-button variant="tertiary" accessibilityLabel={`Review vehicles for year ${year}`}
                                commandFor={yearCheckModalId} command="--hide"
                                onClick={() => navigate(`/vehicles?${new URLSearchParams({ year })}`)}>
                                Review vehicles
                            </s-button>
                        </s-stack>
                    ))}
                </s-stack>
                <s-button slot="primary-action" variant="primary" commandFor={yearCheckModalId} command="--hide">Done</s-button>
            </s-modal>
        </s-stack>
    );
}
